import { ChevronLeft, ChevronRight, Target, TrendingDown, Shield, AlertTriangle, CreditCard, DollarSign, Info } from 'lucide-react';

const PricingTable = ({ 
  selectedCard, 
  selectedSpread, 
  selectedSteps, 
  selectedDemand, 
  currency, 
  selectedPlatforms, 
  priceSlideIndex, 
  setPriceSlideIndex,
  setSelectedSpread,
  setSelectedSteps,
  setSelectedDemand 
}) => {
  // Dynamic pricing based on selections
  const getBasePricing = () => {
    if (selectedCard === 'instant') {
      return [
        { sizeValue: 2500, price: 26, originalPrice: 52, promoCode: 'NEXXOFIRST' },
        { sizeValue: 5000, price: 42, originalPrice: 84, promoCode: 'NEXXOFIRST' },
        { sizeValue: 10000, price: 66, originalPrice: 132, promoCode: 'NEXXOFIRST' },
        { sizeValue: 25000, price: 125, originalPrice: 250, promoCode: 'NEXXOFIRST' },
        { sizeValue: 50000, price: 230, originalPrice: 460, promoCode: 'NEXXOFIRST' },
      ];
    }
    if (selectedCard === 'prime') {
      return [
        { sizeValue: 5000, price: 30, originalPrice: 60, promoCode: 'NEXXOFIRST' },
        { sizeValue: 10000, price: 55, originalPrice: 110, promoCode: 'NEXXOFIRST' },
        { sizeValue: 25000, price: 115, originalPrice: 230, promoCode: 'NEXXOFIRST' },
        { sizeValue: 50000, price: 180, originalPrice: 360, promoCode: 'NEXXOFIRST' },
        { sizeValue: 100000, price: 340, originalPrice: 680, promoCode: 'NEXXOFIRST' },
        { sizeValue: 200000, price: 650, originalPrice: 1300, promoCode: 'NEXXOFIRST' },
      ];
    }
    return [
      { sizeValue: 10000, price: 26, originalPrice: 52, promoCode: 'NEXXOFIRST' },
      { sizeValue: 25000, price: 52.50, originalPrice: 105, promoCode: 'NEXXOFIRST' },
      { sizeValue: 50000, price: 105, originalPrice: 210, promoCode: 'NEXXOFIRST' },
      { sizeValue: 100000, price: 200, originalPrice: 400, promoCode: 'NEXXOFIRST' },
    ];
  };

  const getDynamicPricing = () => {
    const basePricing = getBasePricing();
    
    return basePricing.map(tier => {
      let adjustedPrice = tier.price;
      
      // For Instant and Prime plans, keep pricing static
    //   if (selectedCard !== 'instant' && selectedCard !== 'prime') {
    //     if (selectedSteps === 'three') {
    //       adjustedPrice = Math.round(adjustedPrice * 0.85);
    //     }
    //     if (selectedSpread === 'variable') {
    //       adjustedPrice = Math.round(adjustedPrice * 0.95);
    //     }
    //     if (selectedDemand === 'on') {
    //       adjustedPrice = Math.round(adjustedPrice * 1.2);
    //     }
    //   }
      
      // Convert account size display (only symbol change)
      const convertAccountSize = (value, targetCurrency) => {
        const symbols = {
          USD: '$',
          EUR: '€',
          GBP: '£'
        };
        
        let formattedValue = value.toString();
        if (value >= 1000) {
          const thousands = value / 1000;
          formattedValue = Number.isInteger(thousands)
            ? `${Math.round(thousands)}K`
            : `${(Math.round(thousands * 10) / 10).toFixed(1)}K`;
        }
        return `${symbols[targetCurrency]}${formattedValue}`;
      };
      
      return {
        ...tier,
        size: convertAccountSize(tier.sizeValue, currency),
        price: adjustedPrice,
        originalPrice: tier.originalPrice ?? tier.price
      };
    });
  };

  const pricingTiers = getDynamicPricing();

  // Currency conversion function (only symbol change)
  const convertPrice = (price, targetCurrency) => {
    const symbols = {
      USD: '$',
      EUR: '€',
      GBP: '£'
    };
    
    return `${symbols[targetCurrency]}${price}`;
  };

  // Dynamic features based on selections
  const getDynamicFeatures = () => {
    // Currency-specific base features
    const getCurrencySpecificFeatures = () => {
      const currencyFeatures = {
        USD: {
          payouts: 'Bi-weekly (USD)',
          newsRule: '5 min before USD news',
          tradingHours: 'US Market Hours',
          support: '24/7 US Support'
        },
        EUR: {
          payouts: 'Bi-weekly (EUR)',
          newsRule: '5 min before EUR news',
          tradingHours: 'EU Market Hours',
          support: '24/7 EU Support'
        },
        GBP: {
          payouts: 'Bi-weekly (GBP)',
          newsRule: '5 min before GBP news',
          tradingHours: 'UK Market Hours',
          support: '24/7 UK Support'
        }
      };
      return currencyFeatures[currency];
    };

    const currencyFeatures = getCurrencySpecificFeatures();
    
    if (selectedCard === 'instant') {
      return [
        { name: 'Daily Drawdown', value: '3%' },
        { name: 'Max Drawdown', value: '6%' },
        { name: `Min Withdrawal`, value: '5%' },
        { name: 'Consistency Rule', value: '25%' },
        { name: 'Profit Split', value: '80%' },
        { name: 'One-Time Fee', value: 'Yes' },
      ];
    }

    if (selectedCard === 'prime') {
      return [
        { name: 'Step 1 Profit Target', value: '8%' },
        { name: 'Step 2 Profit Target', value: '5%' },
        { name: 'Daily Drawdown', value: '4%' },
        { name: 'Max Drawdown', value: '10%' },
        { name: 'News Rule (Funded)', value: '5 min' },
        { name: 'Payouts', value: 'Bi-weekly' },
        { name: 'Profit Split', value: '80%' },
        { name: 'One-Time Fee', value: 'Yes' },
      ];
    }

    if (selectedCard === 'power') {
      return [
        { name: 'Step 1 Profit Target', value: '6%' },
        { name: 'Step 2 Profit Target', value: '6%' },
        { name: 'Step 3 Profit Target', value: '4%' },
        { name: 'Max Drawdown', value: '8%' },
        { name: 'Consistency Rule', value: '35%' },
        { name: 'On Demand', value: 'No' },
        { name: 'Profit Split', value: '80%' },
        { name: 'One-Time Fee', value: 'Yes' },
      ];
    }

    const baseFeatures = [
      { name: 'Daily Drawdown', value: '4%' },
      { name: 'Max Drawdown', value: '10%' },
      { name: 'News Rule (Funded)', value: currencyFeatures.newsRule },
      { name: 'Payouts', value: currencyFeatures.payouts },
      { name: 'Profit Split', value: '80%' },
      { name: 'One-Time Fee', value: 'No' },
    ];

    // Add step-based profit targets (only for Prime and Power cards)
    if ((selectedCard === 'prime' || selectedCard === 'power') && selectedSteps === 'two') {
      baseFeatures.unshift(
        { name: 'Step 1 Profit Target', value: '8%' },
        { name: 'Step 2 Profit Target', value: '5%' }
      );
    } else if ((selectedCard === 'prime' || selectedCard === 'power') && selectedSteps === 'three') {
      baseFeatures.unshift(
        { name: 'Step 1 Profit Target', value: '6%' },
        { name: 'Step 2 Profit Target', value: '6%' },
        { name: 'Step 3 Profit Target', value: '4%' }
      );
    }

    return baseFeatures;
  };

  const features = getDynamicFeatures();

  // Function to get icon for each feature
  const getFeatureIcon = (featureName) => {
    const iconMap = {
      'Step 1 Profit Target': Target,
      'Step 2 Profit Target': Target,
      'Step 3 Profit Target': Target,
      'Daily Drawdown': TrendingDown,
      'Max Drawdown': Shield,
      'News Rule (Funded)': AlertTriangle,
      'News Rule': AlertTriangle,
      'Payouts': CreditCard,
      'Profit Split': DollarSign,
      'Profit Share': DollarSign,
      'Payout Threshold': CreditCard,
      'Consistency': Info,
      'One-Time Fee': CreditCard,
    };

    return iconMap[featureName] || Info;
  };

  return (
    <>
      {/* Pricing - Mobile/Tablet Carousel */}
      <div className="lg:hidden mb-12 relative">
        {(() => {
          const tiers = pricingTiers;
          const current = tiers[priceSlideIndex % tiers.length];
          const goPrev = () => setPriceSlideIndex((i) => (i - 1 + tiers.length) % tiers.length);
          const goNext = () => setPriceSlideIndex((i) => (i + 1) % tiers.length);
          return (
            <div className="relative">
              {/* Tier chips selector */}
              <div className="mb-4 px-6 flex gap-2 overflow-x-auto">
                {tiers.map((t, i) => (
                  <button key={i} onClick={() => setPriceSlideIndex(i)} className={`whitespace-nowrap px-4 py-2 rounded-lg border text-sm font-semibold ${i === priceSlideIndex ? 'bg-cyan-400 text-gray-900 border-cyan-400 shadow' : 'bg-gray-800 text-gray-200 border-gray-700'}`}>
                    {t.size}
                  </button>
                ))}
              </div>
              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800 shadow-lg mx-6">
                <div className="text-xs font-semibold text-gray-400 mb-1">{selectedCard ? `${selectedCard.toUpperCase()} Plan` : 'Select a plan'}</div>
                <div className="text-xl font-bold text-white mb-1">{current.size}</div>
                <div className="text-xs text-cyan-400 font-semibold mb-3">Evaluation</div>
                <div className="mb-2">
                  <div className={`text-2xl font-bold ${selectedCard === 'prime' ? (
                    current.featured ? 'text-purple-400' : current.premium ? 'text-yellow-400' : 'text-cyan-400'
                  ) : 'text-cyan-400'}`}>
                    {convertPrice(current.price, currency)}
                  </div>
                  {(selectedCard === 'instant' || selectedCard === 'prime') && current.originalPrice && current.price !== current.originalPrice && (
                    <div className="text-xs text-gray-500">Special Price: <span className="line-through mr-1">{convertPrice(current.originalPrice, currency)}</span> → {convertPrice(current.price, currency)}</div>
                  )}
                </div>
                {/* All table rows as stacked list for mobile/tablet */}
                <ul className="text-left text-xs text-gray-300 space-y-2 mb-4">
                  {features.map((feature, i) => {
                    const IconComponent = getFeatureIcon(feature.name);
                    return (
                      <li key={i} className="flex items-start gap-2">
                        <IconComponent size={12} className="text-cyan-400 mt-0.5" />
                        <span>
                          {feature.name}: <span className="text-gray-400">{feature.value}</span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <button className={`w-full py-2 rounded-lg font-bold transition ${
                  selectedCard === 'prime' ? (
                    current.featured ? 'bg-purple-500 text-white hover:bg-purple-600' :
                    current.premium ? 'bg-yellow-500 text-gray-900 hover:bg-yellow-600' :
                    'bg-cyan-400 text-gray-900 hover:bg-cyan-500'
                  ) : 'bg-cyan-400 text-gray-900 hover:bg-cyan-500'
                }`}>
                  {selectedCard === 'prime' ? (
                    current.featured ? 'Get Popular Plan' :
                    current.premium ? 'Get Premium Plan' :
                    'Start Challenge'
                  ) : 'Start Challenge'}
                </button>
              </div>
              {/* Controls - overlay icons left/right */}
              <button onClick={goPrev} aria-label="Previous" className="absolute top-[calc(50%+10px)] -translate-y-1/2 -left-3 z-10 p-2 rounded-full bg-gray-900/70 border border-gray-700 text-gray-200 shadow hover:bg-gray-800">
                <ChevronLeft size={18} />
              </button>
              <button onClick={goNext} aria-label="Next" className="absolute top-[calc(50%+10px)] -translate-y-1/2 -right-3 z-10 p-2 rounded-full bg-gray-900/70 border border-gray-700 text-gray-200 shadow hover:bg-gray-800">
                <ChevronRight size={18} />
              </button>
            </div>
          );
        })()}
      </div>

      {/* Prime Configuration Options - Only shown when Prime is selected */}
      {selectedCard === 'prime' && (
        <div className="hidden lg:block mb-8">
          <div className="bg-gray-900 rounded-lg p-6 shadow-lg">
            <h3 className="text-lg font-bold text-white mb-4">Prime Configuration Options</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Spreads › Commissions */}
              <div>
                <h4 className="text-sm font-semibold text-gray-300 mb-3">Spreads and commissions</h4>
                <div className="flex gap-2">
                  <button 
                    onClick={() => setSelectedSpread && setSelectedSpread('raw')}
                    className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition h-12 flex items-center justify-center ${
                      selectedSpread === 'raw' ? 'bg-cyan-400 text-gray-900' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    Raw spreads & commissions
                  </button>
                  <button 
                    onClick={() => setSelectedSpread && setSelectedSpread('variable')}
                    className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition h-12 flex items-center justify-center ${
                      selectedSpread === 'variable' ? 'bg-cyan-400 text-gray-900' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    Variable spreads & no commissions
                  </button>
                </div>
              </div>

              {/* Evaluation Steps */}
              <div>
                <h4 className="text-sm font-semibold text-gray-300 mb-3">Choose number of evaluation steps</h4>
                <div className="flex gap-2">
                  <button 
                    onClick={() => setSelectedSteps && setSelectedSteps('two')}
                    className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition h-12 flex items-center justify-center ${
                      selectedSteps === 'two' ? 'bg-cyan-400 text-gray-900' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    Two step
                  </button>
                  <button 
                    onClick={() => setSelectedSteps && setSelectedSteps('three')}
                    className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition relative h-12 flex items-center justify-center ${
                      selectedSteps === 'three' ? 'bg-cyan-400 text-gray-900' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    Three step
                    <span className="absolute -top-1 -right-1 bg-green-500 text-gray-900 text-xs px-2 py-0.5 rounded-full font-semibold">EASIEST</span>
                  </button>
                </div>
              </div>

              {/* On-demand */}
              <div>
                <h4 className="text-sm font-semibold text-gray-300 mb-3">On-demand</h4>
                <div className="flex gap-2">
                  <button 
                    onClick={() => setSelectedDemand && setSelectedDemand('on')}
                    className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition h-12 flex items-center justify-center ${
                      selectedDemand === 'on' ? 'bg-cyan-400 text-gray-900' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    On
                  </button>
                  <button 
                    onClick={() => setSelectedDemand && setSelectedDemand('off')}
                    className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition h-12 flex items-center justify-center ${
                      selectedDemand === 'off' ? 'bg-cyan-400 text-gray-900' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    Off
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Table - Desktop */}
      <div className="hidden lg:block overflow-x-auto rounded-lg bg-gray-900 p-6 mb-12 shadow-lg">
        <div className="mb-4 text-center">
          <h2 className="text-2xl font-bold mb-2">
            {selectedCard ? `${selectedCard.toUpperCase()} Plan Pricing` : 'Select a plan to view pricing'}
          </h2>
          {selectedCard === 'instant' && (
            <p className="text-gray-400 text-sm">
              Showing Nexxo Instant special pricing. Discounts are applied at checkout.
              {selectedPlatforms[selectedCard] && (
                <span className="text-cyan-400 ml-2">• {selectedPlatforms[selectedCard]} Platform</span>
              )}
            </p>
          )}
          {selectedCard !== 'instant' && (
            <p className="text-gray-400 text-sm">
              {currency} Currency • {selectedSteps === 'three' ? '3-Step (15% off)' : '2-Step'} •
              {selectedSpread === 'variable' ? ' Variable Spreads (5% off)' : ' Raw Spreads'} •
              {selectedDemand === 'on' ? ' On-demand (+20%)' : ' Standard'}
              {selectedCard && selectedPlatforms[selectedCard] ? ` • ${selectedPlatforms[selectedCard]} Platform` : ''}
            </p>
          )}
        </div>
       <table className="w-full min-w-[700px] table-auto">
         <thead>
           <tr className="border-b border-gray-800">
             <th></th>
             {pricingTiers.map((tier, idx) => (
               <th key={idx} className="text-center py-4 px-2">
                 <div className="text-xl font-bold text-white">{tier.size}</div>
                 <div className="text-xs text-cyan-400 font-semibold">
                   {selectedCard === 'instant' ? 'Nexxo Instant Evaluation' : 
                    selectedCard === 'prime' ? 'Nexxo Prime Evaluation' : 'Nexxo Power Evaluation'}
                 </div>
                 <div className="mt-2">
                   <div className="text-lg font-bold text-cyan-400">
                     {convertPrice(tier.price, currency)}
                   </div>
                  {(selectedCard === 'instant' || selectedCard === 'prime' || selectedCard === 'power') && tier.originalPrice && tier.price !== tier.originalPrice && (
                    <div className="text-xs text-gray-500 line-through">
                      {convertPrice(tier.originalPrice, currency)}
                    </div>
                  )}
                 </div>
               </th>
             ))}
           </tr>
         </thead>
           <tbody>
             {features.map((feature, idx) => {
               const IconComponent = getFeatureIcon(feature.name);
               return (
                 <tr key={idx} className="border-b border-gray-800">
                   <td className="py-4 px-4 text-sm font-semibold flex items-center gap-2">
                     <IconComponent size={16} className="text-cyan-400" />
                     {feature.name}
                   </td>
                   {pricingTiers.map((tier, i) => (
                     <td key={i} className="text-center py-4 px-2">
                       <span className={`${
                         feature.name.includes('Profit Target') ? 'text-green-400 font-semibold' :
                         feature.name.includes('Daily Drawdown') || feature.name.includes('Max Drawdown') ? 'text-red-400 font-semibold' :
                         feature.name.includes('News Rule') ? 'text-yellow-400 font-semibold' :
                         feature.name.includes('Payouts') || feature.name.includes('Profit Split') || feature.name.includes('One-Time Fee') || feature.name.includes('Profit Share') ? 'text-cyan-400 font-semibold' :
                         'text-gray-300'
                       }`}>
                         {feature.value}
                       </span>
                     </td>
                   ))}
                 </tr>
               );
             })}
             <tr>
               <td className="py-4 px-4 font-semibold text-white">Final Price</td>
               {pricingTiers.map((tier, idx) => (
                 <td key={idx} className="text-center py-4 px-2">
                <div className="mb-2">
                  <div className="text-xl font-bold text-cyan-400">
                    {convertPrice(tier.price, currency)}
                  </div>
                  {(selectedCard === 'instant' || selectedCard === 'prime' || selectedCard === 'power') && tier.originalPrice && tier.price !== tier.originalPrice && (
                    <div className="text-sm text-gray-500">
                      Special Price: {convertPrice(tier.originalPrice, currency)} → {convertPrice(tier.price, currency)}
                    </div>
                  )}
                </div>
                {(selectedCard === 'instant' || selectedCard === 'prime' || selectedCard === 'power') && (
                  <div className="text-xs text-gray-400 mb-3">
                    <div className="text-gray-500 italic mb-2">Added at checkout:</div>
                    <div className="text-green-400 font-semibold">
                      {selectedCard === 'prime' ? 'NEXXOFIRST' : 
                       selectedCard === 'power' ? 'NEXXOFIRST' : tier.promoCode}
                    </div>
                  </div>
                )}
                  <button className="w-full bg-cyan-400 text-gray-900 py-2 rounded-lg font-bold hover:bg-cyan-500 transition">
                    Buy Challenge
                  </button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

    </>
  );
};

export default PricingTable;
